/**
 * @file Renders the modal that sends the DocuSign subscription link to the investor.
 */
import { useState } from "react";
import { Modal } from "antd";
import type { ModalProps } from "antd";
import { useOutletContext } from "react-router-dom";

import { Markdown } from "@components/Text";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { resetInvestmentSubscriptionOperation as resetOperation } from "@api/services/investments";

import type { ModalRef, Investment, OutletContext, ReadInvestment } from "../../types";

const LABEL_TEXT = `Send the link for **[NAME]** to sign the subscription
documents to [EMAIL].

The process may take some time, so please be patient and do not close the
window.`;

const LABEL_NO_TEMPLATE = `\nProject doesn't have PPM template linked which is
required to generate subscription documents for the investor.`;

const LABEL_SEND = "Send";
const LABEL_TITLE = "Send DocuSign Link";
const LABEL_SEND_SUCCESS = "DocuSign link is sent to [NAME]";

type Props = {
  modal: ModalRef;
  investment: Investment;
  readInvestment: ReadInvestment;
};

/**
 * Renders the confirmation modal that resets and resends the investment's DocuSign link.
 */
const SignUrlShareModal = ({ modal, investment, readInvestment }: Props) => {
  const { request, showSuccessMessage } = useAppContext() as AppContext;
  const { getProject, computeAccountFullname } = useOutletContext<OutletContext>();

  const [ isOpen, setIsOpen ] = useState(false);
  const [ isSending, setIsSending ] = useState(false);

  const { id, investorAccountId, projectId, investor } = investment;
  const project = getProject(projectId);
  const ppmTemplateId = project?.ppmTemplateId;

  const isDisabled = !ppmTemplateId;

  const fullName = computeAccountFullname(investorAccountId);
  const email = investor?.email;

  const isClosable = !isSending;

  /**
   * Closes the modal and resets the sending state.
   */
  const onCancel = () => {
    setIsOpen(false);
    setIsSending(false);
  };

  /**
   * Resets the subscription documents, reloads the investment and shows a success message.
   */
  const onOk = () => {
    setIsSending(true);
    return request(resetOperation, { id })
      .then(() => readInvestment(id))
      .then(() => onCancel())
      .then(() => showSuccessMessage(LABEL_SEND_SUCCESS.replace("[NAME]", fullName)));
  };

  const modalProps: ModalProps = {
    open: isOpen,
    title: LABEL_TITLE,
    okText: LABEL_SEND,
    closable: isClosable,
    maskClosable: false,
    destroyOnClose: true,
    okButtonProps: { loading: isSending, disabled: isDisabled },
    cancelButtonProps: { disabled: isSending },
    onCancel,
    onOk,
  };

  /**
   * Opens the modal.
   */
  modal.current.open = () =>
    setIsOpen(true);

  let text = LABEL_TEXT
    .replace("[NAME]", fullName)
    .replace("[EMAIL]", email);

  if (isDisabled) {
    text = LABEL_NO_TEMPLATE;
  }

  return (
    <Modal {...modalProps}>
      <Markdown text={text} />
    </Modal>
  );
};

export default SignUrlShareModal;
